import { createSlice } from '@reduxjs/toolkit';
import { insertNotifyMeEmail } from '../../utils';
import socketClient from '../../socketClient';
import apiClient from '@miroculus/api-client';
import { TYPE_SCRIPT } from '../../constants';
import { toast } from 'react-toastify';
import { updateMoveBackbone } from '../electrodeLayout';
const SCRIPT_STATUS_STOP = 'stop';
export const IDE_THEMES = {
    light: 'base16-light',
    dark: 'base16-dark',
};
export const LINE_STATUS = {
    RUNNING: 1,
    DONE: 2,
    ERROR: 3,
};
export const EDITOR_STATUS = {
    RUNNING: 1,
    DONE: 2,
    STOP: 3,
    PAUSED: 4,
    ERROR: 5,
};
const initialState = {
    script: '',
    currentLine: {
        number: -1,
        status: 0,
        timestamp: 0,
    },
    status: 0,
    statusTimestamp: 0,
    theme: IDE_THEMES.light,
    hints: [],
};
const codeEditorSlice = createSlice({
    name: 'codeEditor',
    initialState,
    reducers: {
        updateScript: (state, action) => {
            state.script = action.payload;
        },
        playScript: (state) => {
            state.status = EDITOR_STATUS.RUNNING;
        },
        onExecutingLine: {
            reducer: (state, action) => {
                const { line, timestamp } = action.payload;
                const editorIsRunning = state.status === EDITOR_STATUS.RUNNING;
                if (timestamp >= state.currentLine.timestamp && editorIsRunning) {
                    state.currentLine = {
                        number: line,
                        status: LINE_STATUS.RUNNING,
                        timestamp,
                    };
                }
            },
            prepare: (line, timestamp) => ({
                payload: { line, timestamp },
            }),
        },
        onLineError: {
            reducer: (state, action) => {
                const { line, timestamp } = action.payload;
                const editorIsRunning = state.status === EDITOR_STATUS.RUNNING;
                if (timestamp >= state.currentLine.timestamp && editorIsRunning) {
                    state.currentLine = {
                        number: line,
                        status: LINE_STATUS.ERROR,
                        timestamp,
                    };
                    state.status = EDITOR_STATUS.ERROR;
                }
            },
            prepare: (err, line, timestamp) => ({
                payload: { err, line, timestamp },
            }),
        },
        onLineDone: {
            reducer: (state, action) => {
                const { line, timestamp } = action.payload;
                const editorIsRunning = state.status === EDITOR_STATUS.RUNNING;
                if (timestamp >= state.currentLine.timestamp && editorIsRunning) {
                    state.currentLine = {
                        number: line,
                        status: LINE_STATUS.DONE,
                        timestamp,
                    };
                }
            },
            prepare: (line, timestamp) => ({
                payload: { line, timestamp },
            }),
        },
        onScriptEnds: (state, action) => {
            const timestamp = action.payload;
            if (timestamp >= state.statusTimestamp) {
                state.status = EDITOR_STATUS.DONE;
                state.statusTimestamp = timestamp;
                state.currentLine = {
                    number: -1,
                    status: 0,
                    timestamp,
                };
            }
        },
        toggleEditorTheme: (state) => {
            state.theme =
                state.theme === IDE_THEMES.light ? IDE_THEMES.dark : IDE_THEMES.light;
        },
        updateEditorStatus: (state, action) => {
            state.status = action.payload;
        },
        updateHints: (state, action) => {
            state.hints = action.payload;
        },
    },
});
// Export actions
export const { updateScript, playScript, onExecutingLine, onLineError, onLineDone, onScriptEnds, toggleEditorTheme, updateEditorStatus, updateHints, } = codeEditorSlice.actions;
// Thunk Actions (kept as regular thunks due to cross-module dispatches and side effects)
export const loadHints = () => async (dispatch) => {
    try {
        const { body } = await apiClient.get('/cocoscript/hints');
        dispatch(updateHints([body]));
    }
    catch (e) {
        console.error(e);
    }
};
export function changeScript(script, _change) {
    return (dispatch) => {
        dispatch(updateScript(script));
    };
}
export function onScriptPlay() {
    return (dispatch, getState) => {
        const script = getState().codeEditor.script;
        const { email } = getState().auth.user;
        dispatch(updateScript(script));
        dispatch(playScript());
        const updatedScript = insertNotifyMeEmail(script, email || '');
        socketClient.sendMessage({
            script: updatedScript,
            type: TYPE_SCRIPT,
        });
    };
}
export const stopScript = () => (dispatch) => {
    dispatch(updateMoveBackbone([], 0));
    socketClient.sendMessage({
        type: TYPE_SCRIPT,
        status: SCRIPT_STATUS_STOP,
    });
};
export const sendScriptAsQC = () => async (_, getState) => {
    const { auth, codeEditor, devices } = getState();
    const cocoscript = insertNotifyMeEmail(codeEditor.script, auth.user.email || '');
    const deviceId = devices.selectedDeviceId;
    let name = 'QC script';
    const nameRegex = /\/\/ ?name:(.*)/gim;
    const nameResult = nameRegex.exec(cocoscript);
    if (nameResult) {
        name = nameResult[1].trim();
    }
    try {
        await apiClient.post('/cocoscript/send', {
            cocoscript,
            deviceId,
            name,
        });
    }
    catch (e) {
        console.error(e);
        if (e.status) {
            toast.error(`Error sending script: ${e.message} (${e.status})`);
        }
    }
};
export const sendScriptAsProtocol = (name) => async (_, getState) => {
    const { auth, codeEditor, devices } = getState();
    const cocoscript = insertNotifyMeEmail(codeEditor.script, auth.user.email || '');
    const deviceId = devices.selectedDeviceId;
    try {
        await apiClient.post('/cocoscript/send-as-protocol', {
            cocoscript,
            deviceId,
            name,
        });
        toast.success('Script sent as protocol succesfully');
    }
    catch (e) {
        console.error(e);
        if (e.status) {
            toast.error(`Error sending script: ${e.message} (${e.status})`);
        }
    }
};
// Export reducer as default
export default codeEditorSlice.reducer;
